/**
 * What the screens call the Global custom-field presets and their choices.
 *
 * A preset's options are STORED values: stable English text that rules,
 * filters, CSV imports and directory syncs compare (the rule catalog's
 * employment types are `Full-time`, `Part-time`, ...). The HumanFirewall-
 * shipped (Global) presets are library data written by the seed
 * (apps/api/src/database/seed/recipient-field-presets.ts), so their names and
 * choices are shown through these catalogue keys in every language. A Company
 * or Workspace preset's label was typed by a person: it is user text, shown as
 * typed (`<UserText>`). Its choices are too, unless they are the Global
 * vocabulary of the same key (a copy of `person_type`).
 *
 * Keys live in a map, so the static-keys guard cannot see them:
 * lib/recipients/__tests__/preset-option-labels.spec.ts proves every one
 * resolves in en, ar, hi and fr.
 */

type Scope = 'Global' | 'Company' | 'Workspace'

interface PresetLike {
  scope: Scope
  key: string
}

export const GLOBAL_PRESET_LABEL_KEYS: Readonly<Record<string, string>> = {
  employment_type: 'recipients.presetLibrary.presets.employmentType',
  person_type: 'recipients.presetLibrary.presets.personType',
  hire_date: 'recipients.presetLibrary.presets.hireDate',
  cost_center: 'recipients.presetLibrary.presets.costCenter',
  manager_email: 'recipients.presetLibrary.presets.managerEmail',
  external_id: 'recipients.presetLibrary.presets.externalId',
}

export const GLOBAL_PRESET_OPTION_LABEL_KEYS: Readonly<
  Record<string, Readonly<Record<string, string>>>
> = {
  employment_type: {
    'Full-time': 'recipients.presetLibrary.options.fullTime',
    'Part-time': 'recipients.presetLibrary.options.partTime',
    Contract: 'recipients.presetLibrary.options.contract',
    Intern: 'recipients.presetLibrary.options.intern',
    Consultant: 'recipients.presetLibrary.options.consultant',
  },
  person_type: {
    Employee: 'recipients.presetLibrary.options.employee',
    Contractor: 'recipients.presetLibrary.options.contractor',
    'Internal vendor': 'recipients.presetLibrary.options.internalVendor',
    'External vendor': 'recipients.presetLibrary.options.externalVendor',
    'Third party': 'recipients.presetLibrary.options.thirdParty',
    'Board member': 'recipients.presetLibrary.options.boardMember',
    Other: 'recipients.presetLibrary.options.other',
  },
}

/** The catalogue key for a Global preset's name, or null (user text). */
export function presetLabelKey(preset: PresetLike): string | null {
  if (preset.scope !== 'Global') return null
  return GLOBAL_PRESET_LABEL_KEYS[preset.key] ?? null
}

/**
 * The catalogue key for a choice of the Global vocabulary under `presetKey`,
 * or null (user text). Keyed by the field key, not the preset's scope: a
 * workspace's copy of `person_type` stores the same values with the same
 * meaning, so `Employee` reads in the viewer's language there too.
 */
export function presetOptionLabelKey(presetKey: string, option: string): string | null {
  return GLOBAL_PRESET_OPTION_LABEL_KEYS[presetKey]?.[option] ?? null
}
